import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import { C } from '../styles/common';

// Admin → Measuring → Measured jobs (the director, 7 Oct 2026; the "Measured
// Jobs" prototype). What the floor recorded, gathered by the thing it would
// change in the app, next to the value the app uses now. Apply takes the
// middle of the counted recordings into the mould, part or machine; Keep
// leaves the old value with a reason; both can be undone. A recording is
// never changed: it can be set aside with a reason, and counted again.

const TEAL = C.teal, AMBER = '#f59e0b', GREEN = '#34d399', DIM = '#9ca3af', SUNK = '#12161b', VIOLET = '#a78bfa';
const KINDS = { mould_change: 'Mould changes', cycle: 'Cycle times', warmup: 'Warm-up', packing: 'Packing', purge: 'Colour purges', cleaning: 'Cleaning', attention: 'Time at the machine', assembly: 'Assembly' };
const ICON = { mould_change: 'fa-right-left', cycle: 'fa-stopwatch', warmup: 'fa-temperature-half', packing: 'fa-box', purge: 'fa-droplet', cleaning: 'fa-broom', attention: 'fa-person', assembly: 'fa-screwdriver-wrench' };
const STATUS = { decide: ['To decide', TEAL], collect: ['Collecting', DIM], applied: ['Applied', GREEN], kept: ['Kept the old value', VIOLET] };
const REC_STATUS = { counted: ['Counted', GREEN], set_aside: ['Set aside', AMBER], abandoned: ['Thrown away', DIM], running: ['Running', TEAL] };
const RECORD_WORD = { mould: 'mould', part: 'part', machine: 'machine', stock_item: 'Build step', product: 'product' };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');

// A value as it reads: minutes whole; a box over a minute as m:ss.
function said(t, v) {
  if (v == null) return 'none';
  if (t.unit === 'min') return `${Math.round(v)} min`;
  if (t.unit === 'min/h') return `${Math.round(v * 10) / 10} min an hour`;
  if (t.unit === '/h') return `${v >= 10 ? Math.round(v) : Math.round(v * 10) / 10} an hour`;
  if ((t.field === 'packing_box_s' || t.field === 'assembly_s') && v >= 60) return `${Math.floor(v / 60)}:${String(Math.round(v % 60)).padStart(2, '0')} min`;
  return `${Math.round(v * 10) / 10} s`;
}
// A decided value as it reads in the app: a level is a level.
const saidApp = (t, v) => (t.field === 'attention_level' ? (v == null ? 'no level' : `level ${v}`) : said(t, v));
function titleOf(t) {
  if (t.record === 'mould') return `${t.name} mould · ${t.words.toLowerCase()}`;
  if (t.field === 'warmup_min') return `Warm-up · ${t.name}`;
  if (t.field === 'packing_box_s') return `Packing · ${t.name}`;
  if (t.field === 'cleaning_min_per_24h') return `Cleaning · ${t.name}`;
  if (t.field === 'purge_min') return `Colour purge · ${t.name}`;
  if (t.field === 'attention_level') return `Time at the machine · ${t.name}`;
  if (t.field === 'per_hour') return `Assembly step · ${t.name}`;
  if (t.field === 'assembly_s') return `Assembly · ${t.name}`;
  return `Cycle time · ${t.name}`;
}
const whereOf = (t) => [...new Set(t.samples.map(s => s.machine).filter(Boolean))].join(', ');
const ASSUMED = { warmup_min: 30, cleaning_min_per_24h: 20 };
const subOf = (t) => (ASSUMED[t.field] != null ? (t.app == null ? `Not set: the app assumes ${ASSUMED[t.field]} min` : 'Set on the machine')
  : t.field === 'purge_min' ? 'The plan does not count purges yet'
  : t.field === 'per_hour' ? 'A person an hour, on its Build step'
  : t.field === 'assembly_s' ? 'Person-time for one'
  : whereOf(t) || KINDS[t.kind]);
// A difference is neither good nor bad; a big one is worth a look.
const diffTone = (d) => (Math.abs(d) >= 10 ? AMBER : DIM);
// The dots in words (the director, 7 Oct): how far from a recommendation.
function dotsTip(t) {
  const off = t.samples.length - t.counted;
  return `${t.counted} of ${t.need} recording${t.need === 1 ? '' : 's'} done. `
    + (t.counted >= t.need ? 'Enough for a recommendation.' : `A recommendation can be made after ${t.need}.`)
    + (off ? ` ${off} set aside, not counted.` : '');
}

export default function MeasuredJobs({ onGoToGiving }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('things');
  const [status, setStatus] = useState('all');
  const [kind, setKind] = useState('all');
  // A link from Insights or the bell names the thing (?key=).
  const [params] = useSearchParams();
  const [sel, setSel] = useState(() => params.get('key'));
  const [wide, setWide] = useState(() => window.innerWidth >= 900);

  const load = useCallback(() => api.get('/api/v1/measuring/measured')
    .then(r => { setData(r.data); return r.data; })
    .catch(err => { setError(err.response?.data?.error || 'The measured jobs could not be read.'); return null; }), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 900);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  // Every action reloads; a refusal says why and the page shows the latest.
  async function act(fn) {
    setBusy(true); setError('');
    try { await fn(); } catch (err) { setError(err.response?.data?.error || 'That did not work. Try again.'); }
    await load();
    setBusy(false);
  }

  if (!data) return error ? <Note tone={AMBER}>{error}</Note> : <div style={{ color: C.muted }}>Loading…</div>;
  const things = data.things || [], recordings = data.recordings || [];

  if (!recordings.length) {
    return (
      <div style={{ ...card, color: DIM, fontSize: 14, lineHeight: 1.55 }}>
        <b style={{ color: C.text }}>Nothing has been measured yet.</b> Measurements you give out on Who measures what appear here once they are recorded: each one next to the value the app uses now, with Apply to take it in.
        {onGoToGiving && <div style={{ marginTop: 10 }}><button onClick={onGoToGiving} style={{ ...btn, ...teal }}>Give out measuring</button></div>}
      </div>
    );
  }

  const count = { all: things.length, decide: 0, collect: 0, applied: 0, kept: 0 };
  things.forEach(t => { count[t.status]++; });
  const shown = things.filter(t => (status === 'all' || t.status === status) && (kind === 'all' || t.kind === kind));
  const current = things.find(t => t.key === sel) || (wide ? shown[0] : null);
  const next = () => { const n = things.find(t => t.status === 'decide' && t.key !== (current && current.key)); setSel(n ? n.key : null); };

  const detail = current && (
    <Detail t={current} busy={busy} reasons={data.set_aside_reasons || []}
      onApply={() => act(() => api.post('/api/v1/measuring/measured/apply', { key: current.key, expect: { value: current.recommendation, was: current.app } }))}
      onKeep={(reason) => act(() => api.post('/api/v1/measuring/measured/keep', { key: current.key, reason }))}
      onUndo={(id) => act(() => api.post(`/api/v1/measuring/measured/decisions/${id}/undo`))}
      onNotYet={next}
      onSetAside={(id, reason, note) => act(() => api.post(`/api/v1/measuring/measurements/${id}/set-aside`, { reason, note }))}
      onCountAgain={(id) => act(() => api.post(`/api/v1/measuring/measurements/${id}/count-again`))} />
  );

  return (
    <>
      {error && <Note tone={AMBER}>{error}</Note>}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }} role="group" aria-label="View">
        {[['things', 'By thing'], ['all', 'All recordings']].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} aria-pressed={view === k}
            style={{ ...chipBtn, borderColor: view === k ? TEAL : C.border, color: view === k ? TEAL : DIM, background: view === k ? C.tealDim : C.surface }}>{l}</button>
        ))}
      </div>

      {view === 'all' ? (
        <AllRecordings recordings={recordings} reasons={data.set_aside_reasons || []} busy={busy}
          onSetAside={(id, reason, note) => act(() => api.post(`/api/v1/measuring/measurements/${id}/set-aside`, { reason, note }))}
          onCountAgain={(id) => act(() => api.post(`/api/v1/measuring/measurements/${id}/count-again`))} />
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center' }} role="group" aria-label="Filter">
            {['all', 'decide', 'collect', 'applied', 'kept'].map(k => {
              const on = status === k, tone = k === 'all' ? C.text : STATUS[k][1];
              return (
                <button key={k} onClick={() => setStatus(k)} aria-pressed={on}
                  style={{ ...chipBtn, padding: '5px 11px', fontSize: 12.5, borderColor: on ? tone : C.border, color: on ? tone : DIM, background: on ? 'rgba(255,255,255,0.04)' : C.surface }}>
                  {k === 'all' ? 'All' : STATUS[k][0]} <b style={{ marginLeft: 4 }}>{count[k]}</b>
                </button>
              );
            })}
            <label htmlFor="mj-kind" style={{ marginLeft: 6, color: DIM, fontSize: 12.5 }}>Kind</label>
            <select id="mj-kind" value={kind} onChange={e => setKind(e.target.value)} style={select}>
              <option value="all">All kinds</option>
              {Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: wide ? 'minmax(0, 5fr) minmax(0, 7fr)' : '1fr', gap: 14, alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {!shown.length && <div style={{ ...card, color: DIM, fontSize: 13 }}>Nothing here with these filters.</div>}
              {shown.map(t => (
                <div key={t.key}>
                  <Row t={t} on={current && current.key === t.key} onClick={() => setSel(current && current.key === t.key && !wide ? null : t.key)} />
                  {!wide && current && current.key === t.key && <div style={{ marginTop: 6 }}>{detail}</div>}
                </div>
              ))}
            </div>
            {wide && <div style={{ position: 'sticky', top: 12 }}>{detail || <div style={{ ...card, color: DIM }}>Choose something on the left.</div>}</div>}
          </div>
        </>
      )}
    </>
  );
}

function Dots({ t }) {
  const tip = dotsTip(t);
  const dots = t.samples.map(s => ({ k: s.measurement_id, off: !s.counted }));
  for (let i = t.counted; i < t.need; i++) dots.push({ k: 'todo' + i, todo: true });
  return (
    <span title={tip} aria-label={tip} style={{ display: 'inline-flex', gap: 3, cursor: 'help' }}>
      {dots.map(d => <i key={d.k} style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
        background: d.todo || d.off ? 'transparent' : TEAL, border: `1.5px solid ${d.todo ? C.border : d.off ? DIM : TEAL}` }} />)}
    </span>
  );
}

function Row({ t, on, onClick }) {
  const [label, tone] = STATUS[t.status];
  return (
    <button onClick={onClick} aria-current={on ? 'true' : undefined}
      style={{ ...card, width: '100%', textAlign: 'left', cursor: 'pointer', display: 'grid', gridTemplateColumns: '34px 1fr auto', gap: '4px 10px', alignItems: 'center',
               borderColor: on ? TEAL : C.border, background: on ? 'rgba(20,184,166,0.07)' : C.surface, color: C.text, padding: '10px 12px' }}>
      <span style={{ gridRow: 'span 2', width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', background: C.tealDim, color: TEAL }}>
        <i className={`fa-solid ${ICON[t.kind] || 'fa-circle'}`} aria-hidden="true" />
      </span>
      <span style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5 }}>{titleOf(t)}</div>
        <div style={{ fontSize: 12, color: DIM }}>{subOf(t)}{t.code && <span style={{ color: C.muted }}> · {t.code}</span>}</div>
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, color: tone, border: `1px solid ${tone}`, borderRadius: 99, padding: '2px 8px', whiteSpace: 'nowrap', justifySelf: 'end' }}>{label}</span>
      <span style={{ gridColumn: '2 / 4', display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center', fontSize: 12, color: DIM, fontVariantNumeric: 'tabular-nums' }}>
        <Dots t={t} />
        {t.counted > 0 && <span>measured <b style={{ color: C.text }}>{t.counted > 1 && t.lo !== t.hi ? `${said(t, t.lo)}–${said(t, t.hi)}` : said(t, t.mid)}</b></span>}
        <span>app <b style={{ color: AMBER }}>{t.app_said || said(t, t.app)}</b></span>
        {t.diff_pct != null && <span style={{ color: diffTone(t.diff_pct), fontWeight: 700 }}>{t.diff_pct > 0 ? '+' : ''}{t.diff_pct}%</span>}
      </span>
    </button>
  );
}

// Recordings against the app's value, on one scale.
function Strip({ t }) {
  // A level is not on the same scale as minutes an hour.
  const assumed = t.app == null && ASSUMED[t.field] != null ? ASSUMED[t.field] : null;
  const appAt = t.field === 'attention_level' ? null : t.app != null ? t.app : assumed;
  const vals = t.samples.map(s => s.value).concat(appAt != null ? [appAt] : []);
  if (!vals.length) return null;
  let lo = Math.min(...vals), hi = Math.max(...vals);
  const pad = (hi - lo) * 0.12 || 1; lo -= pad; hi += pad;
  const W = 640, L = 40, R = 40, x = (v) => L + ((v - lo) / (hi - lo)) * (W - L - R);
  return (
    <svg viewBox={`0 0 ${W} 96`} role="img" aria-label="Recordings against the value the app uses" style={{ width: '100%', height: 'auto', display: 'block' }}>
      <line x1={L} x2={W - R} y1="52" y2="52" stroke={C.border} strokeWidth="2" />
      {t.counted > 1 && <rect x={x(t.lo)} y="44" width={Math.max(2, x(t.hi) - x(t.lo))} height="16" rx="8" fill="rgba(20,184,166,0.18)" />}
      {appAt != null && <>
        <line x1={x(appAt)} x2={x(appAt)} y1="26" y2="70" stroke={AMBER} strokeWidth="2" strokeDasharray="4 3" />
        <text x={x(appAt)} y="18" fill={AMBER} fontSize="13" textAnchor="middle" fontFamily="ui-monospace, monospace">{assumed != null ? `app assumes ${said(t, assumed)}` : `app ${said(t, appAt)}`}</text>
      </>}
      {t.samples.map(s => s.counted
        ? <circle key={s.measurement_id} cx={x(s.value)} cy="52" r="7" fill={TEAL}><title>{`${day(s.at)} · ${s.who || ''} · ${said(t, s.value)}`}</title></circle>
        : <circle key={s.measurement_id} cx={x(s.value)} cy="52" r="7" fill={C.surface} stroke={DIM} strokeWidth="2"><title>{`Set aside: ${s.set_aside_reason || ''}`}</title></circle>)}
      {t.mid != null && <>
        <line x1={x(t.mid)} x2={x(t.mid)} y1="64" y2="74" stroke={TEAL} strokeWidth="2" />
        <text x={x(t.mid)} y="90" fill={TEAL} fontSize="13" textAnchor="middle" fontFamily="ui-monospace, monospace">middle {said(t, t.mid)}</text>
      </>}
    </svg>
  );
}

function Detail({ t, busy, reasons, onApply, onKeep, onUndo, onNotYet, onSetAside, onCountAgain }) {
  const [keeping, setKeeping] = useState(false);
  const [why, setWhy] = useState('');
  useEffect(() => { setKeeping(false); setWhy(''); }, [t.key]);
  const d = t.decision;
  const field = t.words.toLowerCase();
  const stat = (l, v, tone) => (
    <div style={{ minWidth: 76 }}><div style={{ fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{l}</div>
      <div style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: 16, color: tone || C.text }}>{v}</div></div>
  );
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>{KINDS[t.kind]}{whereOf(t) ? ` · ${whereOf(t)}` : ''}</div>
        <h2 style={{ margin: '2px 0', fontSize: 19 }}>{titleOf(t)}</h2>
        <div style={{ fontSize: 13, color: DIM }}>
          {t.writes ? <>Updates the {field} on the {RECORD_WORD[t.record]}</> : <>Kept with the {RECORD_WORD[t.record]}: nothing on the board uses it yet</>}
          {t.code && <span style={{ color: C.muted }}> · {t.code}</span>}
        </div>
      </div>

      <div>
        <Strip t={t} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', marginTop: 6 }}>
          {t.counted > 0 && <>{stat('lowest', said(t, t.lo))}{stat('middle', said(t, t.mid), TEAL)}{stat('highest', said(t, t.hi))}</>}
          {stat('the app uses', t.app_said || said(t, t.app), AMBER)}
          {t.field === 'attention_level' && t.recommendation != null && stat('comes to', saidApp(t, t.recommendation), TEAL)}
          {t.diff_pct != null && stat('difference', `${t.diff_pct > 0 ? '+' : ''}${t.diff_pct}%`)}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
          {t.app == null && ASSUMED[t.field] != null ? `Not set: the app assumes ${ASSUMED[t.field]} min. ` : ''}Hollow dots are set aside and not counted.
        </div>
      </div>

      {/* The decision. */}
      {t.status === 'decide' && (
        <div style={{ ...decision, borderColor: TEAL }}>
          <div style={{ fontWeight: 700 }}>{t.app == null ? `Take ${t.recommendation_said || said(t, t.recommendation)} as the ${field}?` : `Set the ${field} to ${t.recommendation_said || said(t, t.recommendation)}?`}</div>
          <div style={{ fontSize: 13, color: DIM }}>{t.effect} {t.field === 'per_hour' ? `As seen in ${t.counted} recordings, lowest to highest` : t.field === 'attention_level' ? `From the middle of ${t.counted} recordings` : `The middle of ${t.counted} recordings`}{t.writes ? `; it goes into the ${RECORD_WORD[t.record]}’s history as measured.` : '.'}</div>
          {t.impact_text && <div style={{ fontSize: 13, color: C.text }}>{t.impact_text}</div>}
          {keeping ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <input value={why} onChange={e => setWhy(e.target.value)} placeholder="Why the old value stays (shown in the history)" aria-label="Why the old value stays"
                style={{ ...select, flex: '1 1 240px', padding: '7px 9px' }} />
              <button disabled={busy || why.trim().length < 3} onClick={() => onKeep(why)} style={{ ...btn, opacity: why.trim().length < 3 ? 0.5 : 1 }}>Keep it</button>
              <button onClick={() => setKeeping(false)} style={link}>Cancel</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button disabled={busy} onClick={onApply} style={{ ...btn, ...teal }}>Apply</button>
              <button disabled={busy} onClick={onNotYet} style={btn}>Not yet</button>
              <button disabled={busy} onClick={() => setKeeping(true)} style={{ ...btn, background: 'none' }}>Keep the old value…</button>
            </div>
          )}
        </div>
      )}
      {t.status === 'applied' && d && (
        <div style={{ ...decision, borderColor: GREEN }}>
          <div style={{ fontWeight: 700, color: GREEN }}>Applied {day(d.decided_at)}{d.decided_by ? ` by ${d.decided_by}` : ''}: {saidApp(t, Number(d.value))}</div>
          <div style={{ fontSize: 13, color: DIM }}>Was {saidApp(t, d.was == null ? null : Number(d.was))}. {t.writes ? `In the ${RECORD_WORD[t.record]}’s history, with the ${(d.measurement_ids || []).length} recordings it came from.` : ''}</div>
          {(t.new_since > 0 || t.rests_on_set_aside > 0) && (
            <div style={{ fontSize: 13, color: AMBER }}>
              {t.new_since > 0 && `${t.new_since} new recording${t.new_since === 1 ? '' : 's'} since: the middle is now ${said(t, t.mid)}. `}
              {t.rests_on_set_aside > 0 && `${t.rests_on_set_aside} of the recordings it came from ${t.rests_on_set_aside === 1 ? 'was' : 'were'} set aside since. `}
              Undo to decide again with them.
            </div>
          )}
          <div><button disabled={busy} onClick={() => onUndo(d.id)} style={btn}>Undo</button></div>
        </div>
      )}
      {t.status === 'kept' && d && (
        <div style={{ ...decision, borderColor: VIOLET }}>
          <div style={{ fontWeight: 700, color: VIOLET }}>Kept the old value: {t.app_said || said(t, t.app)}</div>
          <div style={{ fontSize: 13, color: DIM }}>“{d.reason}” · {d.decided_by || ''} {day(d.decided_at)}{t.new_since > 0 ? ` · ${t.new_since} new recording${t.new_since === 1 ? '' : 's'} since` : ''}</div>
          <div><button disabled={busy} onClick={() => onUndo(d.id)} style={btn}>Decide again</button></div>
        </div>
      )}
      {t.status === 'collect' && (
        <div style={{ ...decision, borderColor: C.border }}>
          <div style={{ fontWeight: 700 }}>Collecting: {t.counted} of {t.need}</div>
          <div style={{ fontSize: 13, color: DIM }}>
            A value is suggested after {t.need} counted recordings. The app proposes {t.need - t.counted} more on Who measures what when the board next has one; you can also ask for one there.
          </div>
        </div>
      )}

      {/* Assembly: who, how long, and where the time goes. */}
      {t.kind === 'assembly' && t.by_person && t.by_person.length > 1 && (
        <div>
          <div style={sect}>By person</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13 }}>
            {t.by_person.map(p => <span key={p.who}><b>{p.who}</b> <span style={{ fontFamily: 'ui-monospace, monospace' }}>{said(t, p.mid)}</span> <span style={{ color: C.muted }}>· {p.n} recording{p.n === 1 ? '' : 's'}</span></span>)}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>A new person still learning: set their recordings aside, so they do not change the value, and ask for another in a few weeks to see the speed come up.</div>
        </div>
      )}
      {t.getting_ready_min != null && <div style={{ fontSize: 13, color: DIM }}>Getting ready takes about <b style={{ color: C.text }}>{Math.round(t.getting_ready_min)} min</b> a batch, kept apart from the time above.</div>}
      {t.estimates && t.estimates.length > 0 && (
        <div style={{ fontSize: 13, color: DIM }}>
          So about {t.estimates.map((e, i) => <span key={e.label}>{i ? ', ' : ''}<b style={{ color: C.text }}>{e.person_min >= 60 ? `${Math.floor(e.person_min / 60)} h ${e.person_min % 60} min` : `${e.person_min} min`}</b> for {e.label}</span>)} of person-time.
        </div>
      )}
      {t.where_time && (
        <div>
          <div style={sect}>Where the time goes · {t.where_time.who || 'someone'}, {day(t.where_time.at)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {t.where_time.steps.map(s => (
              <div key={s.name} style={{ fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}><span>{s.name}</span><span style={{ fontFamily: 'ui-monospace, monospace' }}>{said({ field: 'assembly_s', unit: 's' }, s.person_s)}<span style={{ color: C.muted }}> · {s.share}%</span></span></div>
                <div style={{ height: 6, borderRadius: 3, background: SUNK, marginTop: 3, overflow: 'hidden' }}><div style={{ height: '100%', width: `${s.share}%`, background: TEAL }} /></div>
              </div>
            ))}
          </div>
          {(() => { const top = t.where_time.steps.slice().sort((a, b) => b.share - a.share)[0]; return top ? <div style={{ fontSize: 13, color: DIM, marginTop: 8 }}><b style={{ color: C.text }}>{top.name}</b> takes {top.share}% of the time: the step to look at first if this needs to get faster.</div> : null; })()}
        </div>
      )}

      <div>
        <div style={sect}>Recordings · never edited or deleted</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {t.samples.slice().reverse().map(s => (
            <Sample key={s.measurement_id} s={s} t={t} reasons={reasons} busy={busy} onSetAside={onSetAside} onCountAgain={onCountAgain} />
          ))}
        </div>
      </div>
      {t.past && t.past.length > 0 && (
        <div>
          <div style={sect}>Earlier decisions</div>
          {t.past.map(p => (
            <div key={p.id} style={{ fontSize: 12.5, color: DIM }}>
              {p.decision === 'applied' ? `Applied ${saidApp(t, Number(p.value))}` : `Kept ${saidApp(t, p.was == null ? null : Number(p.was))}`} on {day(p.decided_at)}{p.decided_by ? ` by ${p.decided_by}` : ''}; undone {day(p.undone_at)}{p.undone_by ? ` by ${p.undone_by}` : ''}.
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SetAsideForm({ reasons, busy, onDone, onCancel }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const ready = reason && (reason !== 'Other' || note.trim().length >= 3);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {reasons.map(r => (
          <button key={r} onClick={() => setReason(r)} aria-pressed={reason === r}
            style={{ ...chipBtn, padding: '4px 9px', fontSize: 12, borderColor: reason === r ? AMBER : C.border, color: reason === r ? AMBER : DIM, background: C.surface }}>{r}</button>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder={reason === 'Other' ? 'Why (needed with Other)' : 'A note (optional)'} aria-label="Note"
          style={{ ...select, flex: '1 1 200px', padding: '6px 8px' }} />
        <button disabled={busy || !ready} onClick={() => onDone(reason, note)} style={{ ...btn, opacity: ready ? 1 : 0.5 }}>Set aside</button>
        <button onClick={onCancel} style={link}>Cancel</button>
      </div>
    </div>
  );
}

function Sample({ s, t, reasons, busy, onSetAside, onCountAgain }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: SUNK, border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 10px', fontSize: 13 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', alignItems: 'baseline' }}>
        <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: s.counted ? C.text : DIM, textDecoration: s.counted ? 'none' : 'line-through' }}>{said(t, s.value)}</span>
        <span style={{ color: DIM }}>{day(s.at)} {time(s.at)} · {s.who || 'someone'}{s.machine ? ` · ${s.machine}` : ''}</span>
        {s.detail && <span style={{ color: C.muted, fontSize: 12 }}>{s.detail}</span>}
        <span style={{ marginLeft: 'auto' }}>
          {s.counted
            ? !open && <button disabled={busy} onClick={() => setOpen(true)} style={link}>Set aside…</button>
            : <button disabled={busy} onClick={() => onCountAgain(s.measurement_id)} style={link}>Count it again</button>}
        </span>
      </div>
      {!s.counted && <div style={{ fontSize: 12, color: AMBER, marginTop: 3 }}>Set aside: {s.set_aside_reason}{s.set_aside_note ? `, ${s.set_aside_note}` : ''}{s.set_aside_by ? ` · ${s.set_aside_by} ${day(s.set_aside_at)}` : ''}</div>}
      {s.counted_again && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>Counted again; it was set aside {day(s.set_aside_at)}: {s.set_aside_reason}</div>}
      {open && <SetAsideForm reasons={reasons} busy={busy} onCancel={() => setOpen(false)} onDone={(r, n) => { setOpen(false); onSetAside(s.measurement_id, r, n); }} />}
    </div>
  );
}

function AllRecordings({ recordings, reasons, busy, onSetAside, onCountAgain }) {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={sect}>Every recording, newest first · never edited or deleted</div>
      {recordings.map(r => {
        const [label, tone] = REC_STATUS[r.status] || [r.status, DIM];
        return (
          <div key={r.id} style={{ ...card, padding: '9px 12px', fontSize: 13 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'baseline' }}>
              <span style={{ color: DIM, fontVariantNumeric: 'tabular-nums', minWidth: 104 }}>{day(r.started_at)} {time(r.started_at)}</span>
              <span style={{ fontWeight: 700 }}>{r.title}</span>
              {r.sub && <span style={{ color: DIM }}>{r.sub}</span>}
              <span style={{ color: DIM }}>· {r.who || 'someone'}</span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'baseline' }}>
                {r.said && <b style={{ fontFamily: 'ui-monospace, monospace', textDecoration: r.status === 'counted' ? 'none' : 'line-through', color: r.status === 'counted' ? C.text : DIM }}>{r.said}</b>}
                <span style={{ fontSize: 11, fontWeight: 700, color: tone, border: `1px solid ${tone}`, borderRadius: 99, padding: '1px 7px' }}>{label}</span>
              </span>
            </div>
            {(r.status === 'set_aside' || r.status === 'abandoned' || r.note) && (
              <div style={{ fontSize: 12, color: r.status === 'set_aside' ? AMBER : C.muted, marginTop: 3 }}>
                {r.status === 'set_aside' && `Set aside: ${r.set_aside_reason}${r.set_aside_note ? `, ${r.set_aside_note}` : ''}${r.set_aside_by ? ` · ${r.set_aside_by}` : ''}`}
                {r.status === 'abandoned' && `Thrown away on the floor${r.abandon_reason ? `: ${r.abandon_reason}` : ''}. Never counted.`}
                {r.note && ` Note: ${r.note}`}
              </div>
            )}
            {r.status === 'counted' && r.set_aside_at && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>Counted again; it was set aside {day(r.set_aside_at)}: {r.set_aside_reason}</div>}
            <div style={{ marginTop: 4 }}>
              {r.status === 'counted' && open !== r.id && <button disabled={busy} onClick={() => setOpen(r.id)} style={link}>Set aside…</button>}
              {r.status === 'set_aside' && <button disabled={busy} onClick={() => onCountAgain(r.id)} style={link}>Count it again</button>}
            </div>
            {open === r.id && <SetAsideForm reasons={reasons} busy={busy} onCancel={() => setOpen(null)} onDone={(reason, note) => { setOpen(null); onSetAside(r.id, reason, note); }} />}
          </div>
        );
      })}
    </div>
  );
}

function Note({ tone, children }) {
  return <div style={{ background: SUNK, border: `1px solid ${tone}`, borderRadius: 9, padding: '10px 12px', fontSize: 13, color: C.text, marginBottom: 14 }}>{children}</div>;
}

const card = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 14px', boxSizing: 'border-box' };
const decision = { background: SUNK, border: '1px solid', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 };
const sect = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, margin: '2px 0 8px' };
const btn = { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 12px', color: C.text, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const teal = { background: TEAL, borderColor: TEAL, color: '#0b1215' };
const chipBtn = { border: '1px solid', borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const select = { background: SUNK, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 8px', color: C.text, fontSize: 13 };
const link = { background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12.5, padding: 0, textDecoration: 'underline' };
